import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Stack,
  Tabs,
  Tab,
  Tooltip,
  useTheme,
  useMediaQuery,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  IconButton,
  Collapse,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  ArrowBack,
  Email,
  LocationCity,
  LocationOn,
  Edit,
  AddShoppingCart,
  SupportAgent,
  LocalOffer,
  Phone,
  WhatsApp,
  Language,
  CalendarMonth,
  Link as LinkIcon,
  DeleteOutline,
  ExpandMore,
} from "@mui/icons-material";
import { useClientDetails } from "../../hooks/useClientsDetails";
import { usePageContext } from "../../context/PageContext";
import { fetchData } from "../../api/client";
import AddClientForm from "../../components/AddClientForm";
import { useClientRelationships } from "../../hooks/useClientRelationships";
import { useClients } from "../../hooks/useClients";
import DuplicateClientDialog from "../../components/clients/DuplicateClientDialog";

/** Helpers */
const tipoNumToStr = (v) => (v === 1 ? "Pessoa Jurídica" : v === 0 ? "Pessoa Física" : "");
const statusNumToStr = (v) => (v === 1 ? "Ativo" : v === 0 ? "Inativo" : "");
const isPJ = (tipo) =>
  (typeof tipo === "number" ? tipo === 1 : (tipo || "").toLowerCase().includes("jur"));
const money = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleString("pt-BR");
};
const splitTags = (t) =>
  (t ? String(t).split(/[;,]/).map((x) => x.trim()).filter(Boolean) : []);

/** ====== Status Solicitação (0..5) ====== */
const REQ_STATUS_MAP = {
  0: "Pendente",
  1: "Em atendimento",
  2: "Concluída",
  3: "Contato futuro",
  4: "Perdida",
  5: "Cancelada",
};

const parseReqStatus = (s) => {
  if (s === null || s === undefined) return undefined;
  if (typeof s === "number" && Number.isFinite(s)) return s;
  const str = String(s).trim().toLowerCase();
  const n = Number(str);
  if (!isNaN(n) && n in REQ_STATUS_MAP) return n;
  const found = Object.entries(REQ_STATUS_MAP).find(([, label]) => label.toLowerCase() === str);
  return found ? Number(found[0]) : undefined;
};

const getReqStatusLabel = (s) => {
  const code = parseReqStatus(s);
  return code !== undefined ? REQ_STATUS_MAP[code] : "—";
};

const getReqStatusChipColor = (s) => {
  const code = parseReqStatus(s);
  switch (code) {
    case 0:
      return "warning";
    case 1:
      return "info";
    case 2:
      return "success";
    case 3:
      return "default";
    case 4:
      return "default";
    case 5:
      return "error";
    default:
      return "default";
  }
};

const isOpenStatus = (s) => {
  const code = parseReqStatus(s);
  return code === 0 || code === 1 || code === 3;
};
const isClosedStatus = (s) => {
  const code = parseReqStatus(s);
  return code === 2 || code === 4 || code === 5;
};

/** ====== Status Venda (compras) – usa o MESMO mapeamento das solicitações ====== */
const getSaleStatusLabel = (p) => getReqStatusLabel(p?.status);
const getSaleStatusChipColor = (p) => getReqStatusChipColor(p?.status);

/** ====== Tipo / Subtipos – tenta usar nomes, cai para IDs se não tiver ====== */
const buildSaleTypePath = (p) => {
  if (!p) return "—";

  const parts = [];

  const typeName = p.typeName || p.tipo_nome || p.tipoName;
  const s1Name = p.subtype1Name || p.subtipo1_nome;
  const s2Name = p.subtype2Name || p.subtipo2_nome;
  const s3Name = p.subtype3Name || p.subtipo3_nome;

  if (typeName) parts.push(typeName);
  if (s1Name) parts.push(s1Name);
  if (s2Name) parts.push(s2Name);
  if (s3Name) parts.push(s3Name);

  // se já tiver nomes, usa só eles
  if (parts.length) return parts.join(" • ");

  // fallback: mostra IDs como estava antes
  const ids = [];
  if (p.id_tipo != null) ids.push(`T${p.id_tipo}`);
  if (p.id_subtipo1 != null) ids.push(`S1-${p.id_subtipo1}`);
  if (p.id_subtipo2 != null) ids.push(`S2-${p.id_subtipo2}`);
  if (p.id_subtipo3 != null) ids.push(`S3-${p.id_subtipo3}`);
  return ids.length ? ids.join(" • ") : "—";
};

const buildProductsSummary = (p) => {
  if (!p) return "—";

  // se vier o array de itens do backend (items_json -> items)
  if (Array.isArray(p.items) && p.items.length > 0) {
    const first = p.items[0];
    const name = first.name || first.nome || first.product || p.product || "—";
    const qty = first.qty || first.quantidade || 1;
    const extra = p.items.length > 1 ? ` + ${p.items.length - 1} produto(s)` : "";
    return `${qty}× ${name}${extra}`;
  }

  // fallback: usa campo product textual
  return p.product || "—";
};

const getOrderCode = (p) => {
  if (!p) return "—";
  if (p.orderCode) return p.orderCode;
  if (p.codigo) return p.codigo;
  return `#${p.id}`;
};

/** ID da pesquisa (definição), não da sessão de resposta — vem do GET /clients/:id/details */
const getSurveyDefinitionId = (s) => s?.survey_id ?? s?.surveyId ?? null;

/** ====== Linha do tempo — unificação (rótulos amigáveis) ====== */
const MERGE_FIELD_LABELS_PT = {
  nome: "Nome",
  apelido: "Apelido",
  email: "E-mail",
  telefone: "Telefone",
  celular: "Celular",
  whatsapp: "WhatsApp",
  cpf: "CPF",
  cnpj: "CNPJ",
  razaosocial: "Razão social",
  razao_social: "Razão social",
  endereco: "Endereço",
  numero: "Número",
  complemento: "Complemento",
  bairro: "Bairro",
  cidade: "Cidade",
  uf: "UF",
  cep: "CEP",
  tags: "Tags",
  id_grupo: "Nível de consciência (grupo)",
  potencial: "Potencial",
  imagem: "Foto do perfil",
  biografia: "Biografia",
  website: "Site",
  profissao: "Profissão",
  cargo: "Cargo",
  sexo: "Sexo",
  data_nascimento: "Data de nascimento",
  estado_civil: "Estado civil",
  id_interno: "ID interno",
  observacao: "Observações",
};

const mergeFieldLabelPt = (key) => {
  if (!key) return "";
  if (MERGE_FIELD_LABELS_PT[key]) return MERGE_FIELD_LABELS_PT[key];
  const s = String(key).replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const formatMergeValue = (v) => {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 240);
  const s = String(v).trim();
  if (!s) return "—";
  if (s.length > 120) return `${s.slice(0, 117)}…`;
  return s;
};

const MERGE_TIMELINE_TECH_FIELDS = new Set([
  "observacao",
  "data_ultima_alteracao",
  "id_usuario_ultima_alteracao",
  "data_cadastro",
  "excluido",
]);

function MergeTimelineEvent({ event: t }) {
  const [open, setOpen] = useState(false);
  const resolved = Array.isArray(t?.meta?.resolvedEntries) ? t.meta.resolvedEntries : [];
  const auto = Array.isArray(t?.meta?.autoLabels) ? t.meta.autoLabels : [];

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip size="small" label={t.type} color="info" variant="outlined" />
          <Typography variant="subtitle2" sx={{ mr: 1, flex: 1, minWidth: 200 }}>
            {t.title}
          </Typography>
          <Tooltip title="Data do evento">
            <CalendarMonth fontSize="small" />
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            {fmtDate(t.date)}
          </Typography>
          {t.status ? (
            <Chip size="small" label={t.status} color="default" variant="outlined" />
          ) : null}
        </Stack>

        {(resolved.length > 0 || auto.length > 0) && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              size="small"
              onClick={() => setOpen((v) => !v)}
              endIcon={
                <ExpandMore
                  sx={{
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 150ms ease",
                  }}
                />
              }
              sx={{ textTransform: "none", px: 0.5 }}
            >
              {open ? "Ocultar detalhes" : "Ver detalhes"}
            </Button>
            {!open && resolved.length ? (
              <Typography variant="caption" color="text.secondary">
                {resolved.length} campo(s) escolhido(s)
                {auto.length ? ` · ${auto.length} preenchido(s) automaticamente` : ""}
              </Typography>
            ) : null}
          </Stack>
        )}

        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
            {resolved.length ? (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                  Escolhidos na unificação
                </Typography>
                <Stack spacing={0.5}>
                  {resolved.map((row) => (
                    <Typography key={row.key} variant="body2">
                      <strong>{row.label}:</strong> {row.value}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            ) : null}
            {auto.length ? (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                  Preenchidos automaticamente (cadastro principal estava vazio)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {auto.join(", ")}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}

/** ====== Estilos ====== */
const cardBaseSx = {
  height: "100%",
  "& .MuiCardContent-root": { display: "flex", flexDirection: "column", gap: 0.5, py: 2.25 },
};
const kpiNumberSx = { fontWeight: 800, lineHeight: 1.1 };
const sectionTitleSx = { mb: 1, fontWeight: 700, letterSpacing: 0.2 };

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useClientDetails(id);
  const { currentPage } = usePageContext();
  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const downMd = useMediaQuery(theme.breakpoints.down("md"));

  // Estados de criação de solicitações
  const [creatingRel, setCreatingRel] = useState(false);
  const [creatingSale, setCreatingSale] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Estados de edição de cliente
  const [editingClient, setEditingClient] = useState(null);
  const [isProcessingEdit, setIsProcessingEdit] = useState(false);
  const [editError, setEditError] = useState(null);
  const [dupDialog, setDupDialog] = useState({
    open: false,
    duplicates: [],
    attemptedPayload: null,
    secondaryClientId: null,
  });
  const [relDialogOpen, setRelDialogOpen] = useState(false);
  const [relType, setRelType] = useState("outro");
  const [relBidirectional, setRelBidirectional] = useState(true);
  const [relNotes, setRelNotes] = useState("");
  const [relClientSearch, setRelClientSearch] = useState("");
  const [relSelectedClient, setRelSelectedClient] = useState(null);
  const [relError, setRelError] = useState(null);

  // Normalizações
  const client = useMemo(() => data?.client ?? {}, [data]);
  const requests = useMemo(() => data?.requests ?? [], [data]);
  const purchases = useMemo(() => data?.purchases ?? [], [data]);
  const surveys = useMemo(() => data?.surveys ?? [], [data]);

  const _tipo = useMemo(
    () => (typeof client.tipo === "number" ? tipoNumToStr(client.tipo) : client.tipo || "—"),
    [client.tipo]
  );
  const _status = useMemo(
    () => (typeof client.status === "number" ? statusNumToStr(client.status) : client.status || "—"),
    [client.status]
  );
  const _isPJ = useMemo(() => isPJ(client.tipo ?? _tipo), [client.tipo, _tipo]);

  const nome = useMemo(
    () => client.nome || (_isPJ ? (client.razao_social ?? client.razaosocial) : "") || "Nome não disponível",
    [client.nome, client.razao_social, client.razaosocial, _isPJ]
  );
  const email = client.email || "Não informado";
  const cidade = client.cidade || "Não informado";
  const estado = client.uf || "Não informado";
  const imagem = client.imagem || "";
  const razaoSocial = client.razao_social ?? client.razaosocial ?? "—";
  const cnpj = client.cnpj || "—";
  const cpf = client.cpf || "—";
  const dataNasc = client.data_nascimento ? fmtDate(client.data_nascimento) : "—";
  const sexo = client.sexo || "—";
  const estadoCivil = client.estado_civil || "—";
  const telefone = client.telefone || "—";
  const whatsapp = client.whatsapp || client.numero_whatsapp || "—";
  const linkedin = client.linkedin || client.id_linkedin || "";
  const website = client.website || "";
  const tags = useMemo(() => splitTags(client.tags), [client.tags]);
  const origem = client.origem;
  const endereco = {
    cep: client.cep,
    logradouro: client.endereco,
    numero: client.numero,
    bairro: client.bairro,
    cidade: client.cidade,
    uf: client.uf,
    complemento: client.complemento,
  };

  // KPIs
  const { purchasesCount, purchasesTotal, purchasesAvg, lastPurchaseDate, openReq, closedReq } = useMemo(() => {
    const count = purchases.length;
    const total = purchases.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
    const avg = count ? total / count : 0;
    const lastDate = purchases.reduce((acc, p) => {
      const d = new Date(p.date);
      return isNaN(d.getTime()) ? acc : (!acc || d > acc ? d : acc);
    }, null);
    const open = requests.filter((r) => isOpenStatus(r.status)).length;
    const closed = requests.filter((r) => isClosedStatus(r.status)).length;

    return {
      purchasesCount: count,
      purchasesTotal: total,
      purchasesAvg: avg,
      lastPurchaseDate: lastDate ? fmtDate(lastDate) : "—",
      openReq: open,
      closedReq: closed,
    };
  }, [purchases, requests]);

  // Timeline
  const timeline = useMemo(() => {
    const items = [
      ...(String(client?.observacao || "")
        .split("\n")
        .map((line) => String(line || "").trim())
        .filter((line) => line.includes("Unificação de cliente:"))
        .map((line, idx) => {
          const m = line.match(/^\[(.+?)\]\s+Unificação de cliente:\s+(.*)$/);
          const date = m?.[1] || null;
          const rawJson = m?.[2] || "";
          let details = null;
          try {
            details = rawJson ? JSON.parse(rawJson) : null;
          } catch {
            details = null;
          }
          const secId = details?.secondary?.id ?? details?.secondaryId ?? null;
          const primId = details?.primary?.id ?? details?.primaryId ?? client?.id ?? null;
          const secName = details?.secondary?.nome || null;
          const primName = details?.primary?.nome || null;
          const resolvedFieldsObj =
            details?.resolvedFields && typeof details.resolvedFields === "object"
              ? details.resolvedFields
              : {};
          const resolvedKeys = Array.isArray(details?.resolvedFieldsList)
            ? details.resolvedFieldsList
            : Object.keys(resolvedFieldsObj);
          const resolvedEntries = resolvedKeys.map((key) => ({
            key,
            label: mergeFieldLabelPt(key),
            value: formatMergeValue(
              Object.prototype.hasOwnProperty.call(resolvedFieldsObj, key)
                ? resolvedFieldsObj[key]
                : undefined
            ),
          }));

          const autoRaw = Array.isArray(details?.autoCopied) ? details.autoCopied : [];
          const autoLabels = Array.from(
            new Set(
              autoRaw
                .filter((k) => k && !MERGE_TIMELINE_TECH_FIELDS.has(String(k)))
                .map((k) => mergeFieldLabelPt(String(k)))
            )
          );

          const title =
            secId != null && primId != null
              ? `Cliente #${secId}${secName ? ` (${secName})` : ""} unificado em #${primId}${primName ? ` (${primName})` : ""}`
              : "Unificação de cliente";

          const statusParts = [];
          if (details?.by) statusParts.push(details.by);
          const status = statusParts.length ? `Por ${statusParts.join(" • ")}` : "Unificação";
          return {
            type: "Unificação",
            date,
            title,
            status,
            statusColor: "info",
            meta: {
              resolvedEntries,
              autoLabels,
            },
            id: `merge-${client?.id || "x"}-${idx}`,
          };
        })),
      ...requests.map((r) => ({
        type: "Solicitação",
        date: r.lastUpdate || r.data || r.createdAt,
        title: r.subject || "—",
        status: getReqStatusLabel(r.status),
        statusColor: getReqStatusChipColor(r.status),
        id: r.id,
        linkTo: "chat",
        linkId: r.id,
      })),
      ...purchases.map((p) => ({
        type: "Compra",
        date: p.date || p.data || p.createdAt,
        title: p.product || "—",
        status: p.amount ? money(p.amount) : "—",
        id: p.id,
        linkTo: "pipeline",
        linkId: p.id,
      })),
      ...surveys.map((s) => {
        const defId = getSurveyDefinitionId(s);
        return {
          type: "Pesquisa",
          date: s.responseDate || s.data || s.createdAt,
          title: s.title || "—",
          status: "Respondida",
          id: s.id,
          ...(defId != null ? { linkTo: "survey", linkId: defId } : {}),
        };
      }),
    ].filter((x) => x.date);

    items.sort((a, b) => new Date(b.date) - new Date(a.date));
    return items.slice(0, 20);
  }, [client?.observacao, client?.id, requests, purchases, surveys]);

  const {
    data: relationships = [],
    isLoading: isRelationshipsLoading,
    createMutation: createRelationshipMutation,
    deleteMutation: deleteRelationshipMutation,
  } = useClientRelationships(id);

  const { data: relatedCandidatesData } = useClients(
    1,
    20,
    relClientSearch,
    client?.id_projeto || null,
    { enabled: relDialogOpen && relClientSearch.trim().length >= 2 }
  );
  const relatedCandidates = (relatedCandidatesData?.clients || []).filter(
    (c) => Number(c.id) !== Number(client?.id)
  );

  const relationshipTypeOptions = [
    { value: "familiar", label: "Familiar" },
    { value: "conjuge", label: "Cônjuge" },
    { value: "responsavel", label: "Responsável" },
    { value: "dependente", label: "Dependente" },
    { value: "socio", label: "Sócio" },
    { value: "contato_comercial", label: "Contato comercial" },
    { value: "indicador", label: "Indicador" },
    { value: "funcionario", label: "Funcionário" },
    { value: "outro", label: "Outro" },
  ];

  const relationshipTypeLabel = (value) =>
    relationshipTypeOptions.find((o) => o.value === value)?.label || value || "Outro";

  const openRelDialog = () => {
    setRelDialogOpen(true);
    setRelError(null);
    setRelType("outro");
    setRelBidirectional(true);
    setRelNotes("");
    setRelSelectedClient(null);
    setRelClientSearch("");
  };

  const closeRelDialog = () => {
    if (createRelationshipMutation.isLoading) return;
    setRelDialogOpen(false);
  };

  const handleCreateRelationship = async () => {
    if (!relSelectedClient?.id) {
      setRelError("Selecione um cliente para vincular.");
      return;
    }
    try {
      setRelError(null);
      await createRelationshipMutation.mutateAsync({
        related_client_id: relSelectedClient.id,
        relationship_type: relType,
        is_bidirectional: relBidirectional,
        notes: relNotes,
      });
      closeRelDialog();
    } catch (e) {
      setRelError(
        e?.response?.data?.message || e?.message || "Não foi possível criar o relacionamento."
      );
    }
  };

  const handleDeleteRelationship = async (relationshipId) => {
    try {
      await deleteRelationshipMutation.mutateAsync(relationshipId);
    } catch (_) {
      // Erro não bloqueante: mantém UX simples.
    }
  };

  const handleOpenSaleInPipeline = (purchase) => {
    if (!purchase) return;
    const id = purchase.pipelineId || purchase.saleId || purchase.id;
    if (!id) return;
    navigate(`/sales-pipeline/${id}`);
  };

  const handleOpenRequestInChat = (request) => {
    if (!request?.id) return;
    navigate(`/chat/${encodeURIComponent(String(request.id))}`);
  };

  const handleOpenSurveyAnalytics = (s) => {
    const surveyId = getSurveyDefinitionId(s);
    if (!surveyId) return;
    navigate(`/surveys/${surveyId}/analytics`);
  };

  const handleTimelineItemNavigate = (t) => {
    if (!t?.linkTo) return;
    if (t.linkTo === "chat" && t.linkId != null) {
      navigate(`/chat/${encodeURIComponent(String(t.linkId))}`);
      return;
    }
    if (t.linkTo === "pipeline" && t.linkId != null) {
      navigate(`/sales-pipeline/${t.linkId}`);
      return;
    }
    if (t.linkTo === "survey" && t.linkId != null) {
      navigate(`/surveys/${t.linkId}/analytics`);
    }
  };

  const handleOpenRelatedClient = (rel) => {
    if (!rel || !client?.id) return;
    const isFrom = Number(rel.client_id_from) === Number(client.id);
    const otherId = isFrom ? rel.client_id_to : rel.client_id_from;
    if (otherId == null) return;
    navigate(`/clients/${otherId}`);
  };

  // Utils
  const getProjectIdFromSession = () => {
    try {
      const raw = sessionStorage.getItem("selectedCompanies");
      const arr = JSON.parse(raw || "[]");
      if (Array.isArray(arr) && arr.length > 0) return Number(arr[0]);
    } catch {}
    return undefined;
  };

  const openPopup = (conversationId) => {
    const width = window.screen.width;
    const height = window.screen.height;
    window.open(
      `/popup-call/${conversationId}`,
      "SolicitaçãoPopup",
      `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
    );
  };

  // Navegação / edição
  const handleBack = () => navigate(`/clients?page=${currentPage}`);

  const openClientForEdit = async () => {
    if (!client?.id) return;
    try {
      setIsProcessingEdit(true);
      setEditError(null);
      const resp = await fetchData(`/clients/${client.id}`, "GET");
      const fullClient = (resp && (resp.data || resp)) || {};
      setEditingClient(fullClient);
    } catch (e) {
      setEditError(
        e?.message ||
          "Não foi possível carregar os dados completos do cliente."
      );
      setEditingClient(client);
    } finally {
      setIsProcessingEdit(false);
    }
  };

  const handleCloseEditForm = () => {
    setEditingClient(null);
    setEditError(null);
  };

  const handleSubmitEditForm = async (payload) => {
    try {
      setIsProcessingEdit(true);
      setEditError(null);
      const method = payload?.id ? "PUT" : "POST";
      const endpoint = payload?.id ? `/clients/${payload.id}` : `/clients`;
      await fetchData(endpoint, method, payload);
      setEditingClient(null);
      await refetch?.();
    } catch (e) {
      if (e?.code === "errors.clients.duplicate") {
        setDupDialog({
          open: true,
          duplicates: e?.data?.duplicates || [],
          attemptedPayload: payload || null,
          secondaryClientId: payload?.id ? Number(payload.id) : null,
        });
        return;
      }
      setEditError(e?.message || "Erro ao salvar o cliente.");
    } finally {
      setIsProcessingEdit(false);
    }
  };

  // Novas solicitações
  const onNewRequest = async () => {
    if (!client?.id || creatingRel || creatingSale) return;
    try {
      setCreateError(null);
      setCreatingRel(true);

      const projectId = getProjectIdFromSession();
      const payload = {
        projectId: projectId || undefined,
        clientId: client.id,
        client: { id: client.id },
        typeId: undefined,
        typeKind: "1", // relacionamento
        initialMessage: "",
        source: "client-details",
      };

      const created = await fetchData("/conversations", "POST", payload);
      const conv = created?.data || created;
      if (!conv?.id) throw new Error("A API não retornou o ID da solicitação criada.");

      openPopup(conv.id);
    } catch (e) {
      setCreateError(
        e?.message ||
          "Não foi possível criar a solicitação."
      );
    } finally {
      setCreatingRel(false);
    }
  };

  const onNewPurchase = async () => {
    if (!client?.id || creatingRel || creatingSale) return;
    try {
      setCreateError(null);
      setCreatingSale(true);

      const projectId = getProjectIdFromSession();
      const payload = {
        projectId: projectId || undefined,
        clientId: client.id,
        client: { id: client.id },
        typeId: undefined,
        typeKind: "2", // comercial (venda)
        initialMessage: "",
        source: "client-details",
      };

      const created = await fetchData("/conversations", "POST", payload);
      const conv = created?.data || created;
      if (!conv?.id) throw new Error("A API não retornou o ID da compra/solicitação criada.");

      openPopup(conv.id);
    } catch (e) {
      setCreateError(
        e?.message ||
          "Não foi possível criar a venda."
      );
    } finally {
      setCreatingSale(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <Typography color="error" variant="h6">
          Erro ao carregar os detalhes do cliente. Por favor, tente novamente.
        </Typography>
      </Box>
    );
  }

  // Se estiver editando, mostra o formulário
  if (editingClient) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
        {editError && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error">{editError}</Alert>
          </Box>
        )}
        <AddClientForm
          client={editingClient}
          isFetching={isProcessingEdit}
          onClose={handleCloseEditForm}
          onSubmit={handleSubmitEditForm}
          onDuplicateDetected={({ duplicates, attemptedPayload }) => {
            setDupDialog({
              open: true,
              duplicates,
              attemptedPayload: attemptedPayload || null,
              secondaryClientId: editingClient?.id ? Number(editingClient.id) : null,
            });
          }}
        />

        <DuplicateClientDialog
          open={dupDialog.open}
          duplicates={dupDialog.duplicates}
          attemptedPayload={dupDialog.attemptedPayload}
          secondaryClientId={dupDialog.secondaryClientId}
          secondaryLabel={
            dupDialog.secondaryClientId ? "cliente que você estava editando" : "novo cadastro"
          }
          onChangePhone={() => {
            setDupDialog((s) => ({ ...(s || {}), open: false }));
            setEditError("Altere o telefone para continuar.");
          }}
          onClose={() => setDupDialog((s) => ({ ...(s || {}), open: false }))}
          onMerged={({ primaryId }) => {
            setDupDialog((s) => ({ ...(s || {}), open: false }));
            setEditingClient(null);
            refetch?.();
            if (primaryId) navigate(`/clients/${primaryId}`);
          }}
        />
      </Box>
    );
  }

  return (
    <Box className="tour-client-detail-root" sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      {/* HEADER */}
      <Paper className="tour-client-detail-header" variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
          <Avatar
            sx={{ width: 64, height: 64, bgcolor: "primary.main", fontSize: "1.5rem" }}
            alt={nome}
            src={imagem}
          >
            {imagem ? null : nome?.[0] || "?"}
          </Avatar>

          <Box sx={{ minWidth: 260, flex: 1 }}>
            <Typography variant={downMd ? "h5" : "h4"} fontWeight={800} lineHeight={1.1}>
              {nome}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, flexWrap: "wrap" }}>
              <Chip size="small" label={`ID: ${client.id || "—"}`} />
              <Chip size="small" label={`Tipo: ${_tipo}`} />
              <Chip size="small" label={`Status: ${_status}`} />
              {origem != null && (
                <Chip size="small" icon={<LocalOffer fontSize="small" />} label={`Origem: ${origem}`} />
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} sx={{ ml: "auto" }} flexWrap="wrap">
            <Button variant="outlined" size="small" startIcon={<ArrowBack />} onClick={handleBack}>
              Voltar
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Edit />}
              onClick={openClientForEdit}
              disabled={isProcessingEdit}
            >
              {isProcessingEdit ? "Abrindo..." : "Editar"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LinkIcon />}
              onClick={openRelDialog}
            >
              Vincular
            </Button>
            <Button
              variant="contained"
              size="small"
              color="info"
              startIcon={<SupportAgent />}
              onClick={onNewRequest}
              disabled={creatingRel || creatingSale}
            >
              {creatingRel ? "Criando..." : "Nova Solicitação"}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={<AddShoppingCart />}
              onClick={onNewPurchase}
              disabled={creatingRel || creatingSale}
            >
              {creatingSale ? "Criando..." : "Nova Compra"}
            </Button>
          </Stack>
        </Stack>

        {(createError || editError) && (
          <Box mt={1.5}>
            {createError && <Alert severity="error">{createError}</Alert>}
            {editError && <Alert severity="error">{editError}</Alert>}
          </Box>
        )}
      </Paper>

      {/* KPIs */}
      <Grid className="tour-client-detail-kpis" container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Card sx={cardBaseSx} elevation={0} variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Valor Total Comprado</Typography>
              <Typography variant="h5" sx={kpiNumberSx}>{money(purchasesTotal)}</Typography>
              <Typography variant="caption" color="text.secondary">Ticket médio: {money(purchasesAvg)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={cardBaseSx} elevation={0} variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Compras</Typography>
              <Typography variant="h5" sx={kpiNumberSx}>{purchasesCount}</Typography>
              <Typography variant="caption" color="text.secondary">Última: {lastPurchaseDate}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={cardBaseSx} elevation={0} variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Solicitações</Typography>
              <Stack direction="row" spacing={2} alignItems="baseline">
                <Typography variant="h5" sx={kpiNumberSx}>{openReq}</Typography>
                <Typography variant="body2" color="text.secondary">abertas</Typography>
                <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{closedReq}</Typography>
                <Typography variant="body2" color="text.secondary">fechadas</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={cardBaseSx} elevation={0} variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Contato</Typography>
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Email fontSize="small" /><Typography variant="body2">{email}</Typography>
                </Stack>
                {telefone && telefone !== "—" && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Phone fontSize="small" /><Typography variant="body2">{telefone}</Typography>
                  </Stack>
                )}
                {whatsapp && whatsapp !== "—" && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WhatsApp fontSize="small" /><Typography variant="body2">{whatsapp}</Typography>
                  </Stack>
                )}
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocationCity fontSize="small" /><Typography variant="body2">{cidade}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocationOn fontSize="small" /><Typography variant="body2">{estado}</Typography>
                </Stack>
                {website && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Language fontSize="small" /><Typography variant="body2">{website}</Typography>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Identificação & Endereço */}
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} md={6}>
          <Card sx={cardBaseSx} elevation={0} variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={sectionTitleSx}>Identificação</Typography>
              <Grid container spacing={1.25}>
                {_isPJ ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Razão Social</Typography>
                      <Typography variant="body1">{razaoSocial}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">CNPJ</Typography>
                      <Typography variant="body1">{cnpj}</Typography>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">CPF</Typography>
                      <Typography variant="body1">{cpf}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Data de Nascimento</Typography>
                      <Typography variant="body1">{dataNasc}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Sexo</Typography>
                      <Typography variant="body1">{sexo}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Estado Civil</Typography>
                      <Typography variant="body1">{estadoCivil}</Typography>
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Tipo</Typography>
                  <Typography variant="body1">{_tipo}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body1">{_status}</Typography>
                </Grid>
                {linkedin && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">LinkedIn</Typography>
                    <Typography variant="body1">{linkedin}</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={cardBaseSx} elevation={0} variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={sectionTitleSx}>Endereço</Typography>
              <Grid container spacing={1.25}>
                {endereco.cep && (
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">CEP</Typography>
                    <Typography variant="body1">{endereco.cep}</Typography>
                  </Grid>
                )}
                {endereco.logradouro && (
                  <Grid item xs={12} sm={8}>
                    <Typography variant="caption" color="text.secondary">Logradouro</Typography>
                    <Typography variant="body1">{endereco.logradouro}</Typography>
                  </Grid>
                )}
                {endereco.numero && (
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Número</Typography>
                    <Typography variant="body1">{endereco.numero}</Typography>
                  </Grid>
                )}
                {endereco.bairro && (
                  <Grid item xs={12} sm={8}>
                    <Typography variant="caption" color="text.secondary">Bairro</Typography>
                    <Typography variant="body1">{endereco.bairro}</Typography>
                  </Grid>
                )}
                {(endereco.cidade || endereco.uf) && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Cidade/UF</Typography>
                    <Typography variant="body1">
                      {endereco.cidade || "—"} {endereco.uf ? `- ${endereco.uf}` : ""}
                    </Typography>
                  </Grid>
                )}
                {endereco.complemento && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Complemento</Typography>
                    <Typography variant="body1">{endereco.complemento}</Typography>
                  </Grid>
                )}
              </Grid>

              {!!tags.length && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Tags</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {tags.map((t) => (
                      <Chip key={t} label={t} size="small" />
                    ))}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Abas */}
      <Paper variant="outlined" sx={{ mt: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1 }}
        >
          <Tab label={`Solicitações (${requests.length})`} />
          <Tab label={`Compras (${purchases.length})`} />
          <Tab label={`Pesquisas (${surveys.length})`} />
          <Tab label="Linha do tempo" />
          <Tab label={`Relacionamentos (${relationships.length})`} />
        </Tabs>
        <Divider />

        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 96 }}>ID</TableCell>
                    <TableCell>Assunto</TableCell>
                    <TableCell sx={{ width: 220 }}>Status</TableCell>
                    <TableCell sx={{ width: 220 }}>Última Atualização</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.length ? (
                    requests.map((r) => (
                      <TableRow
                        key={r.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleOpenRequestInChat(r)}
                      >
                        <TableCell>{r.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap title={r.subject || "—"}>
                            {r.subject || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={getReqStatusChipColor(r.status)}
                            label={getReqStatusLabel(r.status)}
                          />
                        </TableCell>
                        <TableCell>{fmtDate(r.lastUpdate)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Nenhuma solicitação encontrada.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 120 }}>Pedido</TableCell>
                    <TableCell>Produtos</TableCell>
                    <TableCell sx={{ width: 220 }}>Tipo / Subtipos</TableCell>
                    <TableCell sx={{ width: 140 }}>Status</TableCell>
                    <TableCell sx={{ width: 140 }}>Valor</TableCell>
                    <TableCell sx={{ width: 200 }}>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchases.length ? (
                    purchases.map((p) => {
                      const itemsCount = Array.isArray(p.items)
                        ? p.items.length
                        : (p.items_count != null ? p.items_count : null);

                      return (
                        <TableRow
                          key={p.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleOpenSaleInPipeline(p)}
                        >
                          <TableCell>
                            {/* NÃO REPETE mais o protocolo */}
                            <Typography variant="body2" fontWeight={600}>
                              {getOrderCode(p)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="body2"
                              title={buildProductsSummary(p)}
                              sx={{ maxWidth: 320 }}
                              noWrap
                            >
                              {buildProductsSummary(p)}
                            </Typography>
                            {itemsCount != null && itemsCount > 1 && (
                              <Typography variant="caption" color="text.secondary">
                                {itemsCount} itens
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ maxWidth: 260 }}
                              noWrap
                              title={buildSaleTypePath(p)}
                            >
                              {buildSaleTypePath(p)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              color={getSaleStatusChipColor(p)}
                              label={getSaleStatusLabel(p)}
                            />
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {money(p.amount)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {fmtDate(p.date || p.data || p.createdAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Nenhuma compra encontrada.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 96 }}>ID</TableCell>
                    <TableCell>Pesquisa</TableCell>
                    <TableCell sx={{ width: 240 }}>Data da Resposta</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {surveys.length ? (
                    surveys.map((s) => {
                      const surveyDefId = getSurveyDefinitionId(s);
                      return (
                        <TableRow
                          key={s.id}
                          hover
                          sx={{ cursor: surveyDefId ? "pointer" : "default" }}
                          onClick={() => surveyDefId && handleOpenSurveyAnalytics(s)}
                        >
                          <TableCell>{s.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap title={s.title || "—"}>
                            {s.title || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>{fmtDate(s.responseDate)}</TableCell>
                      </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        Nenhuma pesquisa encontrada.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 3 && (
          <Box sx={{ p: 2 }}>
            {timeline.length ? (
              <Stack spacing={1.25}>
                {timeline.map((t) =>
                  t.type === "Unificação" ? (
                    <MergeTimelineEvent key={`${t.type}-${t.id}`} event={t} />
                  ) : (
                    <Paper
                      key={`${t.type}-${t.id}`}
                      variant="outlined"
                      onClick={() => handleTimelineItemNavigate(t)}
                      sx={{
                        p: 1.25,
                        ...(t.linkTo
                          ? {
                              cursor: "pointer",
                              transition: "box-shadow 120ms ease",
                              "&:hover": { boxShadow: 1 },
                            }
                          : {}),
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" label={t.type} />
                        <Typography variant="subtitle2" sx={{ mr: 1 }}>
                          {t.title}
                        </Typography>
                        <Tooltip title="Data do evento">
                          <CalendarMonth fontSize="small" />
                        </Tooltip>
                        <Typography variant="body2" color="text.secondary">
                          {fmtDate(t.date)}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        {t.status && (
                          <Chip
                            size="small"
                            label={t.status}
                            color={t.type === "Solicitação" ? t.statusColor : "default"}
                          />
                        )}
                      </Stack>
                    </Paper>
                  )
                )}
              </Stack>
            ) : (
              <Typography align="center" sx={{ py: 2 }}>
                Sem eventos recentes.
              </Typography>
            )}
          </Box>
        )}
        {tab === 4 && (
          <Box sx={{ p: 2 }}>
            {isRelationshipsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : relationships.length ? (
              <Stack spacing={1.25}>
                {relationships.map((rel) => {
                  const isFrom = Number(rel.client_id_from) === Number(client.id);
                  const otherName = isFrom ? rel.to_name : rel.from_name;
                  const direction = rel.is_bidirectional
                    ? "Bidirecional"
                    : isFrom
                    ? "Origem"
                    : "Destino";
                  return (
                    <Paper
                      key={rel.id}
                      variant="outlined"
                      onClick={() => handleOpenRelatedClient(rel)}
                      sx={{
                        p: 1.25,
                        cursor: "pointer",
                        transition: "box-shadow 120ms ease",
                        "&:hover": { boxShadow: 1 },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" label={relationshipTypeLabel(rel.relationship_type)} />
                        <Chip size="small" variant="outlined" label={direction} />
                        <Typography variant="subtitle2">{otherName || `Cliente #${isFrom ? rel.client_id_to : rel.client_id_from}`}</Typography>
                        <Box sx={{ flex: 1 }} />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRelationship(rel.id);
                          }}
                          disabled={deleteRelationshipMutation.isLoading}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Stack>
                      {rel.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          {rel.notes}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              <Stack alignItems="center" spacing={1.25} sx={{ py: 2 }}>
                <Typography color="text.secondary">Nenhum relacionamento cadastrado.</Typography>
                <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={openRelDialog}>
                  Vincular relacionamento
                </Button>
              </Stack>
            )}
          </Box>
        )}
      </Paper>

      <Dialog open={relDialogOpen} onClose={closeRelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Vincular relacionamento</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {relError && <Alert severity="error">{relError}</Alert>}
            <Autocomplete
              options={relatedCandidates}
              getOptionLabel={(o) => `${o?.nome || "Sem nome"} (#${o?.id || "-"})`}
              value={relSelectedClient}
              onChange={(_e, v) => setRelSelectedClient(v)}
              onInputChange={(_e, v) => setRelClientSearch(v || "")}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              loading={Boolean(relDialogOpen && relClientSearch.trim().length >= 2 && !relatedCandidates.length)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente relacionado"
                  placeholder="Digite nome, email, telefone..."
                  helperText="Pesquise por no mínimo 2 caracteres."
                />
              )}
            />
            <FormControl fullWidth>
              <InputLabel>Tipo de relacionamento</InputLabel>
              <Select
                label="Tipo de relacionamento"
                value={relType}
                onChange={(e) => setRelType(e.target.value)}
              >
                {relationshipTypeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Direção</InputLabel>
              <Select
                label="Direção"
                value={relBidirectional ? "bi" : "uni"}
                onChange={(e) => setRelBidirectional(e.target.value === "bi")}
              >
                <MenuItem value="bi">Bidirecional</MenuItem>
                <MenuItem value="uni">Unidirecional</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Observações"
              multiline
              minRows={3}
              value={relNotes}
              onChange={(e) => setRelNotes(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRelDialog} disabled={createRelationshipMutation.isLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateRelationship}
            disabled={createRelationshipMutation.isLoading}
          >
            {createRelationshipMutation.isLoading ? "Salvando..." : "Vincular"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDetails;
